import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { localeLabels, type Locale } from '@sonasi/shared-types';

import { SearchSharingCard } from '@/components/SearchSharingCard';
import { TranslateWithAi } from '@/components/TranslateWithAi';
import { languageTabs, saveMessage } from '@/lib/apps';
import { previewUrl, useWebsiteUrl } from '@/lib/share-image';
import {
  inLocale,
  useSaveSiteContent,
  useSiteShareImage,
  useSiteContent,
  type FaqRow,
  type PrincipleRow,
} from '@/lib/site-content';

/**
 * The copy on the home page and the Support page.
 *
 * Read from the database on every visit, and until now writable only by running
 * `yarn api:site-import` on a machine holding the database credentials.
 */

interface Draft {
  heroKicker: string;
  heroTitle: string;
  heroSubtitle: string;
  principlesLabel: string;
  principlesTitle: string;
  principles: PrincipleRow[];
  supportSubtitle: string;
  supportContactNote: string;
  supportBusinessNote: string;
  faq: FaqRow[];
}

interface Addresses {
  supportEmail: string;
  businessEmail: string;
}

/** The links box as a list: one address per line, blanks dropped, spaces trimmed. */
const linksOf = (text: string): string[] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

export default function SitePage() {
  const content = useSiteContent();
  const save = useSaveSiteContent();
  const shareImage = useSiteShareImage();
  const website = useWebsiteUrl();

  const [lang, setLang] = useState<Locale>('en');

  const row = content.data;
  const savedDraft = useMemo<Draft | null>(
    () =>
      row
        ? {
            heroKicker: inLocale(row.heroKicker, lang, ''),
            heroTitle: inLocale(row.heroTitle, lang, ''),
            heroSubtitle: inLocale(row.heroSubtitle, lang, ''),
            principlesLabel: inLocale(row.principlesLabel, lang, ''),
            principlesTitle: inLocale(row.principlesTitle, lang, ''),
            principles: inLocale<PrincipleRow[]>(row.principles, lang, []),
            supportSubtitle: inLocale(row.supportSubtitle, lang, ''),
            supportContactNote: inLocale(row.supportContactNote, lang, ''),
            supportBusinessNote: inLocale(row.supportBusinessNote, lang, ''),
            faq: inLocale<FaqRow[]>(row.faq, lang, []),
          }
        : null,
    [row, lang],
  );
  const savedAddresses = useMemo<Addresses | null>(
    () => (row ? { supportEmail: row.supportEmail, businessEmail: row.businessEmail } : null),
    [row],
  );

  const [draft, setDraft] = useState<Draft | null>(savedDraft);
  const [addresses, setAddresses] = useState<Addresses | null>(savedAddresses);
  // One address per line in the box; the list is what is saved.
  const savedLinks = useMemo(() => (row ? (row.links ?? []).join('\n') : null), [row]);
  const [links, setLinks] = useState<string | null>(savedLinks);

  /*
   * The row follows a reload only into an untouched form — or a switched
   * tab, which always loads its own language. The translation modal stays
   * mounted on every tab and reloads the row when it writes; standing on
   * 日本語 with half a sentence typed while that lands, following the row
   * would replace the typing with the model's text, unasked. Same rule as
   * Settings › Legal.
   */
  const touched = useRef(false);
  const shown = useRef(lang);
  useEffect(() => {
    const switched = shown.current !== lang;
    shown.current = lang;
    if (switched || !touched.current) {
      setDraft(savedDraft);
      touched.current = false;
    }
  }, [savedDraft, lang]);
  useEffect(() => setAddresses(savedAddresses), [savedAddresses]);
  useEffect(() => setLinks(savedLinks), [savedLinks]);

  const contentDirty = Boolean(
    draft && savedDraft && JSON.stringify(draft) !== JSON.stringify(savedDraft),
  );
  const addressesDirty = Boolean(
    addresses && savedAddresses && JSON.stringify(addresses) !== JSON.stringify(savedAddresses),
  );

  /*
   * Saved from a button on the page, not from the header's save bar.
   *
   * The design keeps that bar for the app and post editors and gives every
   * Settings screen its own accent button at the end instead — and it is the
   * right split: an editor is a long form somebody scrolls through for
   * minutes, where a Save that scrolls away is a Save that gets missed, while
   * this screen is read and left. `useEditorBar` published from here also put
   * "Unsaved changes" in the header of a screen the design never shows one on.
   */
  const linksDirty = Boolean(
    links !== null && savedLinks !== null && linksOf(links).join('\n') !== savedLinks,
  );

  const mutate = save.mutate;
  const dirty = contentDirty || addressesDirty || linksDirty;
  const commit = useCallback(() => {
    if (!draft || !addresses || links === null) return;
    mutate(
      {
        locale: lang,
        // Only the parts that changed, so saving an address does not rewrite every
        // block of the open language with whatever the form happens to hold.
        content: contentDirty ? draft : undefined,
        addresses: addressesDirty ? addresses : undefined,
        links: linksDirty ? linksOf(links) : undefined,
      },
      // Saved is no longer "typed": the form may follow a reload again.
      { onSuccess: () => (touched.current = false) },
    );
  }, [draft, addresses, links, lang, contentDirty, addressesDirty, linksDirty, mutate]);

  if (content.isPending) {
    return (
      <p className="m-0 text-[13px]" style={{ color: 'var(--muted)' }}>
        Loading…
      </p>
    );
  }
  if (content.isError || !draft || !addresses || links === null || !row) {
    return (
      <p className="m-0 text-[12.5px]" role="alert" style={{ color: 'var(--danger)' }}>
        {saveMessage(content.error)}
      </p>
    );
  }

  const edit = (patch: Partial<Draft>) => {
    touched.current = true;
    setDraft((current) => (current ? { ...current, ...patch } : current));
  };

  const tabs = languageTabs(row.locales);

  const field = (label: string, key: keyof Draft & string, rows = 1) => (
    <label className="flex flex-col gap-1.5">
      <span className="field-label">{label}</span>
      {rows > 1 ? (
        <textarea
          // Keyed by locale so switching tabs remounts the field rather than
          // leaving the previous language's words under the new tab.
          key={`${key}-${lang}`}
          value={draft[key] as string}
          rows={rows}
          onChange={(event) => edit({ [key]: event.target.value } as Partial<Draft>)}
          className="field resize-y"
        />
      ) : (
        <input
          key={`${key}-${lang}`}
          value={draft[key] as string}
          onChange={(event) => edit({ [key]: event.target.value } as Partial<Draft>)}
          className="field"
        />
      )}
    </label>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="panel flex flex-col gap-2.5 px-4 py-3.5">
        <span className="flex flex-wrap items-center gap-2.5">
          <h2 className="eyebrow m-0">Site copy</h2>
          {/*
            Beside the heading, not a paragraph under it. It is one fact about
            how this screen behaves — there is no staging step — and the design
            gives that fact the smallest thing on the card.
          */}
          <span className="ml-auto text-[11px]" style={{ color: 'var(--dim)' }}>
            Read on every visit — a save is live immediately
          </span>
        </span>
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="flex flex-wrap gap-1.5" role="tablist" aria-label="Language">
            {tabs.map((tab) => (
              <button
                key={tab.locale}
                type="button"
                role="tab"
                aria-selected={lang === tab.locale}
                onClick={() => setLang(tab.locale)}
                className="chip focus-ring cursor-pointer"
              >
                <span>{localeLabels[tab.locale]}</span>
                <span className="font-mono text-[9.5px] opacity-75">{tab.percent}%</span>
              </button>
            ))}
          </span>

          {/*
            The editors' button, for the one row the site copy is. English
            only, and only while nothing on screen is unsaved — the run reads
            the row, not the form, and what it writes reloads the row, which
            would take an address half typed with it (the addresses follow a
            reload without the guard the copy has; Em Liên's finding). Mounted
            on every tab so switching to watch a language land does not end
            the run.
          */}
          <span className="ml-auto flex items-center gap-2.5">
            {lang === 'en' && dirty ? (
              <span className="text-[11px]" style={{ color: 'var(--dim)' }}>
                Save your changes before translating.
              </span>
            ) : null}
            <TranslateWithAi kind="site" id="site" offered={lang === 'en' && !dirty} />
          </span>
        </span>
      </div>

      <div className="panel flex flex-col gap-2.5 px-4 py-3.5">
        <h2 className="eyebrow m-0">Home · hero</h2>
        {field('Kicker', 'heroKicker')}
        {field('Title', 'heroTitle')}
        {field('Subtitle', 'heroSubtitle', 2)}
      </div>

      <div className="panel flex flex-col gap-2.5 px-4 py-3.5">
        <span className="flex flex-wrap items-center gap-2.5">
          <h2 className="eyebrow m-0">Home · principles</h2>
          <span className="font-mono text-[9.5px]" style={{ color: 'var(--dim)' }}>
            {draft.principles.length} written
          </span>
          <button
            type="button"
            onClick={() =>
              edit({ principles: [...draft.principles, { title: '', description: '' }] })
            }
            className="btn-small focus-ring ml-auto inline-flex cursor-pointer items-center gap-1"
          >
            <span aria-hidden="true">＋</span>
            Add principle
          </button>
        </span>
        {field('Label', 'principlesLabel')}
        {field('Title', 'principlesTitle')}
        <div data-rows>
          {draft.principles.map((principle, index) => (
            <div
              key={`principle-${lang}-${index}`}
              className="flex flex-wrap items-center gap-2.5 border-b py-2"
              style={{ borderColor: 'var(--line)' }}
            >
              <input
                value={principle.title}
                aria-label={`Principle ${index + 1} title`}
                onChange={(event) =>
                  edit({
                    principles: draft.principles.map((one, at) =>
                      at === index ? { ...one, title: event.target.value } : one,
                    ),
                  })
                }
                className="field min-w-0 flex-[1_1_150px]"
              />
              <input
                value={principle.description}
                aria-label={`Principle ${index + 1} description`}
                onChange={(event) =>
                  edit({
                    principles: draft.principles.map((one, at) =>
                      at === index ? { ...one, description: event.target.value } : one,
                    ),
                  })
                }
                className="field min-w-0 flex-[1_1_220px]"
              />
              <button
                type="button"
                aria-label={`Remove principle ${index + 1}`}
                onClick={() =>
                  edit({ principles: draft.principles.filter((_, at) => at !== index) })
                }
                className="btn-quiet focus-ring cursor-pointer text-[11.5px]"
                style={{ color: 'var(--dim)' }}
              >
                Remove
              </button>
            </div>
          ))}
          {draft.principles.length === 0 ? (
            <p className="m-0 py-2 text-[12.5px]" style={{ color: 'var(--muted)' }}>
              None in this language yet.
            </p>
          ) : null}
        </div>
      </div>

      <div className="panel flex flex-col gap-2.5 px-4 py-3.5">
        <span className="flex flex-wrap items-center gap-2.5">
          <h2 className="eyebrow m-0">Support page</h2>
          <span className="font-mono text-[9.5px]" style={{ color: 'var(--dim)' }}>
            {draft.faq.length} questions
          </span>
          <button
            type="button"
            onClick={() => edit({ faq: [...draft.faq, { question: '', answer: '' }] })}
            className="btn-small focus-ring ml-auto inline-flex cursor-pointer items-center gap-1"
          >
            <span aria-hidden="true">＋</span>
            Add question
          </button>
        </span>
        {field('Subtitle', 'supportSubtitle', 2)}
        {field('Contact note', 'supportContactNote')}
        {field('Business note', 'supportBusinessNote')}
        <div data-rows>
          {draft.faq.map((entry, index) => (
            <div
              key={`faq-${lang}-${index}`}
              className="flex flex-col gap-2 border-b py-2"
              style={{ borderColor: 'var(--line)' }}
            >
              <input
                value={entry.question}
                aria-label={`Question ${index + 1}`}
                onChange={(event) =>
                  edit({
                    faq: draft.faq.map((one, at) =>
                      at === index ? { ...one, question: event.target.value } : one,
                    ),
                  })
                }
                className="field"
              />
              <div className="flex items-start gap-3.5">
                <textarea
                  value={entry.answer}
                  aria-label={`Answer ${index + 1}`}
                  rows={2}
                  onChange={(event) =>
                    edit({
                      faq: draft.faq.map((one, at) =>
                        at === index ? { ...one, answer: event.target.value } : one,
                      ),
                    })
                  }
                  className="field min-w-0 flex-1 resize-y"
                />
                <button
                  type="button"
                  aria-label={`Remove question ${index + 1}`}
                  onClick={() => edit({ faq: draft.faq.filter((_, at) => at !== index) })}
                  className="btn-quiet focus-ring cursor-pointer text-[11.5px]"
                  style={{ color: 'var(--dim)' }}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
          {draft.faq.length === 0 ? (
            <p className="m-0 py-2 text-[12.5px]" style={{ color: 'var(--muted)' }}>
              No questions in this language yet.
            </p>
          ) : null}
        </div>
      </div>

      <div className="panel flex flex-col gap-2.5 px-4 py-3.5">
        <span className="flex flex-wrap items-center gap-2.5">
          <h2 className="eyebrow m-0">Addresses</h2>
          {/*
            Outside the language tabs, deliberately: an address is the same in
            every language, and putting it under a tab would invite the reading
            that a Japanese visitor is given a different one.
          */}
          <span className="ml-auto text-[11px]" style={{ color: 'var(--dim)' }}>
            The same in every language — these are not translated
          </span>
        </span>
        <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="field-label">Support</span>
            <input
              value={addresses.supportEmail}
              onChange={(event) =>
                setAddresses((current) =>
                  current ? { ...current, supportEmail: event.target.value } : current,
                )
              }
              className="field field-mono"
            />
          </label>
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="field-label">Business</span>
            <input
              value={addresses.businessEmail}
              onChange={(event) =>
                setAddresses((current) =>
                  current ? { ...current, businessEmail: event.target.value } : current,
                )
              }
              className="field field-mono"
            />
          </label>
        </div>
      </div>

      {/*
        What a link to the site unfurls with, and where else the studio is.
        Outside the language tabs like the addresses: one picture and one list
        of links for every language.
      */}
      <SearchSharingCard
        lang={lang}
        url={`${website}/en`}
        // The card names the site by its English headline, as the picture does.
        auto={{ title: inLocale(row.heroTitle, 'en', ''), description: '' }}
        value={{ title: '', description: '' }}
        onChange={() => undefined}
        imageOnly
        image={{
          current: row.shareImageUrl,
          generated: previewUrl(website, '/en', row.updatedAt),
          pending: shareImage.isPending,
          error: shareImage.isError ? saveMessage(shareImage.error) : null,
          onChange: (file) => shareImage.mutate(file),
        }}
      >
        <label className="flex flex-col gap-1.5">
          <span className="field-label">Links · one per line</span>
          <textarea
            value={links}
            rows={4}
            onChange={(event) => setLinks(event.target.value)}
            placeholder={'https://apps.apple.com/developer/id…\nhttps://x.com/…'}
            className="field field-mono resize-y text-[11.5px]"
          />
          <span className="text-[11px] leading-[1.5]" style={{ color: 'var(--dim)' }}>
            The studio's other addresses — its App Store developer page, its accounts. Full https://
            links; search engines read them as the same organisation.
          </span>
        </label>
      </SearchSharingCard>

      {/*
        One button for the whole screen, because there is one save behind it —
        the design shows a single card and puts its button inside it; here there
        are five, and a button in the last one would read as saving addresses.
      */}
      <span className="flex flex-wrap items-center gap-2.5">
        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={commit}
          className="btn-accent focus-ring min-h-8 cursor-pointer px-[13px] py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save site copy'}
        </button>
        {dirty ? (
          <span className="font-mono text-[10px]" style={{ color: 'var(--warn)' }}>
            unsaved changes
          </span>
        ) : null}
        {save.isError ? (
          <span className="text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            {saveMessage(save.error)}
          </span>
        ) : null}
      </span>
    </div>
  );
}
